/**
 * SubmitOfferModal — Phase 2A "استقبال العروض"
 * ──────────────────────────────────────────────
 * Marketplace-wide, independent of CARTAL Business.
 * - Auth required to submit an offer.
 * - Amount + optional message + optional phone.
 * - Self-offer & rate-limit are enforced by the backend.
 */
import React, { useState } from "react";
import { X, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { apiClient, formatSAR } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";

export default function SubmitOfferModal({ open, onClose, vehicle, onSubmitted }) {
  const { lang } = useI18n();
  const { user } = useAuth();
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open || !vehicle) return null;

  const submit = async (e) => {
    e.preventDefault();
    const num = Number(amount);
    if (!num || num <= 0) {
      toast.error(lang === "ar" ? "أدخل قيمة عرض صحيحة." : "Enter a valid offer amount.");
      return;
    }
    setBusy(true);
    try {
      await apiClient.post(`/vehicles/${vehicle.id}/offers`, {
        amount_sar: num,
        message: message.trim() || null,
        buyer_phone: phone.trim() || null,
      });
      toast.success(
        lang === "ar"
          ? "تم إرسال عرضك. سيصل البائع فورًا في لوحة إعلانه."
          : "Offer sent. The seller will see it in their listing panel."
      );
      setAmount("");
      setMessage("");
      setPhone("");
      onSubmitted?.();
      onClose?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = (typeof d === "object" ? (lang === "ar" ? d.ar : d.en) : d)
        || (lang === "ar" ? "تعذّر إرسال العرض. حاول لاحقًا." : "Failed to submit. Try again.");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const requiresLogin = !user;

  return (
    <div className="fixed inset-0 z-[100]" data-testid="submit-offer-modal">
      <button
        type="button"
        aria-label="close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
      />
      <div className="absolute inset-x-4 top-16 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:top-24 sm:w-[440px] rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="p-5 flex items-start justify-between border-b border-[#0a0a0a]/[0.06]">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8a6f2e] font-bold">
              {lang === "ar" ? "استقبال العروض" : "Submit an offer"}
            </div>
            <h3 className="text-lg font-display font-bold text-[#0a0a0a] mt-1">
              {lang === "ar" ? "قدّم عرضك" : "Send your offer"}
            </h3>
            <div className="text-[12px] text-[#8a8a8a] mt-1">
              {lang === "ar"
                ? "عرضك خاص بينك وبين البائع فقط."
                : "Your offer is private between you and the seller."}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#8a8a8a] hover:text-[#0a0a0a] shrink-0"
            aria-label="close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {requiresLogin ? (
          <div className="p-6 text-center">
            <div className="text-sm text-[#525252] mb-4">
              {lang === "ar"
                ? "تسجيل الدخول مطلوب لتقديم عرض."
                : "You need to log in to submit an offer."}
            </div>
            <div className="flex gap-2 justify-center">
              <Link
                to={`/login?next=/vehicle/${vehicle.id}`}
                className="inline-flex items-center gap-2 rounded-full h-10 px-5 text-sm font-bold text-white bg-[#0a0a0a]"
                onClick={onClose}
                data-testid="offer-login-link"
              >
                {lang === "ar" ? "تسجيل الدخول" : "Log in"}
              </Link>
              <Link
                to={`/register?next=/vehicle/${vehicle.id}`}
                className="inline-flex items-center gap-2 rounded-full h-10 px-5 text-sm font-bold"
                style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
                onClick={onClose}
                data-testid="offer-register-link"
              >
                {lang === "ar" ? "إنشاء حساب" : "Register"}
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-4">
            <label className="block">
              <div className="text-[11px] font-bold uppercase tracking-widest text-[#8a6f2e] mb-1">
                {lang === "ar" ? "قيمة العرض (ر.س) *" : "Offer amount (SAR) *"}
              </div>
              <input
                type="number"
                inputMode="numeric"
                required
                min="1"
                max="100000000"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={lang === "ar" ? "مثال: 45000" : "e.g. 45000"}
                className="w-full px-3 py-2.5 rounded-lg border border-[#0a0a0a]/15 text-sm font-semibold"
                dir="ltr"
                data-testid="offer-amount"
              />
              {amount && (
                <div className="text-[11px] text-[#525252] mt-1">
                  {formatSAR(Number(amount))} {lang === "ar" ? "ريال" : "SAR"}
                </div>
              )}
            </label>

            <label className="block">
              <div className="text-[11px] font-bold uppercase tracking-widest text-[#8a6f2e] mb-1">
                {lang === "ar" ? "رقم الجوال (اختياري)" : "Phone (optional)"}
              </div>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={lang === "ar" ? "05xxxxxxxx" : "05xxxxxxxx"}
                className="w-full px-3 py-2.5 rounded-lg border border-[#0a0a0a]/15 text-sm"
                dir="ltr"
                data-testid="offer-phone"
              />
            </label>

            <label className="block">
              <div className="text-[11px] font-bold uppercase tracking-widest text-[#8a6f2e] mb-1">
                {lang === "ar" ? "رسالة قصيرة (اختياري)" : "Short message (optional)"}
              </div>
              <textarea
                rows={3}
                maxLength={1000}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={lang === "ar" ? "أي تفاصيل تودّ إضافتها…" : "Any details you'd like to add…"}
                className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm resize-none"
                data-testid="offer-message"
              />
            </label>

            <div className="text-[11px] text-[#525252] bg-[#faf9f5] rounded-lg px-3 py-2 border border-[#0a0a0a]/[0.05]">
              {lang === "ar"
                ? "قبول البائع لعرضك لا يعني بيع المركبة تلقائيًا — القبول موافقة على العرض وسيتم استكمال التواصل بينكما."
                : "Acceptance is a soft approval, not an automatic sale. You'll continue the conversation with the seller."}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-full text-sm text-[#525252] hover:text-[#0a0a0a]"
              >
                {lang === "ar" ? "إلغاء" : "Cancel"}
              </button>
              <button
                type="submit"
                disabled={busy}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold shadow-sm disabled:opacity-50"
                style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
                data-testid="offer-submit"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {lang === "ar" ? "إرسال العرض" : "Send offer"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
